import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Header, Icon } from 'semantic-ui-react';

import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { useDropDownUsers } from '../../services/broker';
import {
  BondInsuranceInvoiceDTO,
  getBondInsuranceInvoiceBillet
} from '../../services/bond-insurance-invoices';
import { dangerNotification, warningNotification } from '../../services/notification';
import { UsersToCustomDropDownOptions } from '../../util';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';

import BondInsuranceInvoicesFiltersBar from './components/BondInsuranceInvoicesFiltersBar';
import BondInsuranceInvoiceDetailsModal from './components/BondInsuranceInvoiceDetailsModal';
import BondInsuranceInvoicesTable from './components/BondInsuranceInvoicesTable';
import {
  bondInsuranceInvoicesLabel,
  initialFilters,
  statusOptions
} from './constants';
import { useBondInsuranceInvoicesList } from './hooks/useBondInsuranceInvoicesList';
import { BondInsuranceInvoiceAction } from './types';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const toDropdownOptions = (options: CustomDropDownOptions[]) =>
  (options || []).map(item => {
    const valueId = typeof item.value === 'string' ? item.value : item.value?.id;

    return {
      key: valueId || String(item.key),
      text: item.text || '',
      value: valueId || ''
    };
  });

const BondInsuranceInvoicesPage = ({
  availableBrokersAndEstates,
  user
}: Props) => {
  const isBackOffice = isBackOfficeUser();
  const isEstateUser = (user?.role || '').includes('ESTATE');
  const showEstateColumn = isBackOffice;
  const [selectedInvoice, setSelectedInvoice] = useState<BondInsuranceInvoiceDTO | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<{
    invoiceId: string;
    action: BondInsuranceInvoiceAction;
  } | null>(null);

  const estateOptions = useMemo(
    () => toDropdownOptions(availableBrokersAndEstates?.estates || []),
    [availableBrokersAndEstates?.estates]
  );

  const insurerOptions = useMemo(() => {
    const bondInsurers = availableBrokersAndEstates?.bondInsurers?.length
      ? availableBrokersAndEstates.bondInsurers
      : (availableBrokersAndEstates?.insurers || []).filter((insurer: CustomDropDownOptions) =>
        insurer.value?.availability?.includes('BOND_INSURANCE')
      );

    return toDropdownOptions(bondInsurers || []);
  }, [availableBrokersAndEstates?.bondInsurers, availableBrokersAndEstates?.insurers]);

  const initialListFilters = useMemo(() => {
    const onlyEstate = estateOptions.length === 1 ? estateOptions[0].value : '';
    const onlyInsurer = insurerOptions.length === 1 ? insurerOptions[0].value : '';

    return {
      ...initialFilters,
      estateId: onlyEstate,
      insurerId: onlyInsurer
    };
  }, [estateOptions, insurerOptions]);

  const {
    isLoading,
    items,
    total,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    handleSearch,
    clearFilters
  } = useBondInsuranceInvoicesList({
    initialFilters: initialListFilters
  });

  const { data: usersData } = useDropDownUsers(user?.broker?.id);

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((accountManager: any) => ({
        id: accountManager.id,
        name: accountManager.name
      }))
    ).map((item: any) => ({
      key: item.value,
      value: item.value,
      text: item.text
    }));
  }, [usersData]);

  const handleDownloadBillet = async (invoice: BondInsuranceInvoiceDTO) => {
    setActionLoading({ invoiceId: invoice.id, action: 'download-billet' });

    try {
      const response = await getBondInsuranceInvoiceBillet(invoice.id);
      const url = response.urls?.[0]?.link;

      if (!response.success || !url) {
        warningNotification('Arquivo não encontrado', 'O boleto ainda não foi disponibilizado.');
        return;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível baixar o boleto.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenDetails = (invoice: BondInsuranceInvoiceDTO) => {
    setSelectedInvoice(invoice);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedInvoice(null);
  };

  return (
    <>
      <Header as="h2">
        <Icon name="file alternate outline" />
        <Header.Content>{bondInsuranceInvoicesLabel}</Header.Content>
      </Header>

      <BondInsuranceInvoicesFiltersBar
        isBackOffice={isBackOffice}
        isEstateUser={isEstateUser}
        estateOptions={estateOptions}
        insurerOptions={insurerOptions}
        accountManagerOptions={accountManagerOptions}
        filters={filters}
        setFilters={setFilters}
        statusOptions={statusOptions}
        onSearch={handleSearch}
        onClear={clearFilters}
        isLoading={isLoading}
      />

      <BondInsuranceInvoicesTable
        isLoading={isLoading}
        items={items}
        currentPage={currentPage}
        total={total}
        setCurrentPage={setCurrentPage}
        showEstateColumn={showEstateColumn}
        userRole={user?.role}
        userEstateUnitId={user?.estateUnitId}
        actionLoading={actionLoading}
        onOpenDetails={handleOpenDetails}
        onDownloadBillet={handleDownloadBillet}
      />

      <BondInsuranceInvoiceDetailsModal
        open={detailsModalOpen}
        invoice={selectedInvoice}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default connect(mapState)(BondInsuranceInvoicesPage);
